import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TextField } from '../../../../modules/design-system';
import {
  placeDetails,
  placesAutocomplete,
  type AddressPrediction,
} from './googlePlaces';
import { parseAddressDetails, type ParsedAddress } from './parseAddressDetails';

export interface AddressAutocompleteProps {
  /** Current text in the search box (controlled by the form). */
  value: string;
  /** Free-text change (typing). */
  onChange: (text: string) => void;
  /** Fired after a prediction is picked AND its details parse — the form
   *  fills line1/city/state/etc. from this. */
  onResolved: (parsed: ParsedAddress) => void;
  label?: string;
  placeholder?: string;
}

/**
 * Google-Places address search — DS `TextField` + the Google Maps JS SDK
 * (browser-direct, no backend). Debounced predictions; the dropdown is
 * rendered in a PORTAL with `position: fixed` so the modal's
 * `overflow-y-auto` can't clip it (it used to get cut off by the card edge).
 * `onMouseDown preventDefault` keeps the click alive through the input blur.
 */
export function AddressAutocomplete({
  value,
  onChange,
  onResolved,
  label = 'Address',
  placeholder = 'Start typing your address…',
}: AddressAutocompleteProps) {
  const [predictions, setPredictions] = useState<AddressPrediction[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | undefined>(undefined);
  const blurRef = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      window.clearTimeout(debounceRef.current);
      window.clearTimeout(blurRef.current);
    },
    [],
  );

  const showDropdown = open && predictions.length > 0;

  // Measure the input so the portaled dropdown sits flush under it, and keep
  // it aligned while the page/modal scrolls or resizes.
  useEffect(() => {
    if (!showDropdown) return;
    const measure = () => {
      if (wrapRef.current) setRect(wrapRef.current.getBoundingClientRect());
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [showDropdown, predictions]);

  const fetchPredictions = (text: string) => {
    window.clearTimeout(debounceRef.current);
    if (!text.trim()) {
      setPredictions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      setLoading(true);
      placesAutocomplete(text)
        .then((preds) => {
          setPredictions(preds);
          setOpen(preds.length > 0);
        })
        .catch(() => setPredictions([]))
        .finally(() => setLoading(false));
    }, 300);
  };

  const handleSelect = async (prediction: AddressPrediction) => {
    onChange(prediction.description);
    setOpen(false);
    setPredictions([]);
    const details = await placeDetails(prediction.placeId);
    const parsed = parseAddressDetails(details);
    if (parsed) onResolved(parsed);
  };

  return (
    <div ref={wrapRef} className="relative">
      <TextField
        label={label}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(v) => {
          onChange(v);
          fetchPredictions(v);
        }}
        onFocus={() => {
          if (predictions.length > 0) setOpen(true);
        }}
        onBlur={() => {
          blurRef.current = window.setTimeout(() => setOpen(false), 150);
        }}
        rightSlot={loading ? <Spinner /> : undefined}
      />
      {showDropdown &&
        rect &&
        createPortal(
          <ul
            className="max-h-60 overflow-auto rounded-[12px] py-1 shadow-[0_12px_40px_rgba(0,0,0,0.18)]"
            style={{
              position: 'fixed',
              top: rect.bottom + 4,
              left: rect.left,
              width: rect.width,
              zIndex: 60,
              background: 'var(--co-TextField-fill, #FFFFFF)',
              border: '1px solid var(--co-TextField-border, #EAEAEB)',
            }}
          >
            {predictions.map((prediction) => (
              <li key={prediction.placeId}>
                <button
                  type="button"
                  // Keep the click from being eaten by the input's blur.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void handleSelect(prediction)}
                  className="block w-full px-3.5 py-2.5 text-left text-[14px] transition-colors hover:bg-black/[0.05]"
                  style={{ color: 'var(--co-TextField-textColor, #15181E)' }}
                >
                  {prediction.description}
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
      style={{ color: 'rgba(21,24,30,0.35)' }}
    />
  );
}
